import type {PanelBottomPill} from '@Components/panel/components/panel-bottom-pills';
import type {PanelFooterPrimaryActionType} from '@Components/panel/components/panel-footer-primary-actions';
import {PanelFooter, PanelFooterPrimaryCustomAction, PanelFooterPrimaryDefaultAction} from '@Components/panel';
import {ButtonSize} from '@Components/button';
import type {ReactElement} from 'react';
import {getSelectedGridItems} from '@Components/base-grid';
import type {GridItemStorage} from '@Components/base-grid/components/grid-item';
import type {StockPrimaryCTA} from '@Panels/stock-panel/stock-panel.types';
import {StockPanelTabs} from '@Panels/stock-panel/stock-panel.types';
import {getBottomPills} from '@Panels/stock-panel/stock-panel.library';
import {noop} from '@Utils/general.util';
import {useAppSelector} from '@/hooks';
import {PosterEditorTestId} from '@/test-ids/poster-editor-test-id';
import styles from './stock-panel-footer.module.scss';

export interface StockPanelFooterProps {
  selectionGroupID: string;
  ctaText: string;
  showCount?: boolean;
  bottomPillItems?: PanelBottomPill[];
  onPrimaryAction: StockPrimaryCTA;
  makeBottomPillsScrollable: boolean;
  /** An extra action on the selection, shown before the CTA; null when the host has none for these items. */
  getActionBeforeCta?: (selectedItems: GridItemStorage[]) => ReactElement | null;
}

const ALL_PILLS_LENGTH = getBottomPills(StockPanelTabs.PHOTOS, noop).length;

export default function StockPanelFooter({showCount = true, ...props}: StockPanelFooterProps): ReactElement {
  const selectedItems = useAppSelector((state) => {
    return getSelectedGridItems(state, props.selectionGroupID);
  });

  const getPrimaryAction = (): PanelFooterPrimaryActionType[] | undefined => {
    if (selectedItems.length === 0) {
      return undefined;
    }

    return [
      <PanelFooterPrimaryCustomAction key="stock-selection-actions">
        <div className={styles.selectionActions}>
          {props.getActionBeforeCta?.(selectedItems)}
          <PanelFooterPrimaryDefaultAction
            data-testid={PosterEditorTestId.ADD_SELECTED_BUTTON}
            text={showCount ? `${props.ctaText} (${selectedItems.length})` : props.ctaText}
            size={ButtonSize.MEDIUM}
            isFullWidth
            onClick={(): void => {
              void props.onPrimaryAction(selectedItems);
            }}
          />
        </div>
      </PanelFooterPrimaryCustomAction>,
    ];
  };

  return (
    <PanelFooter
      key={props.selectionGroupID}
      bottomPills={props.bottomPillItems}
      isScrollable={props.bottomPillItems && props.makeBottomPillsScrollable && props.bottomPillItems.length >= ALL_PILLS_LENGTH}
      className={styles.footerSeparator}
      primaryActionsClassName=""
      actionsContainerClassName="spacing-p-4"
      primaryActions={getPrimaryAction()}
      fullWidthPrimaryActions
    />
  );
}
